// Stargazing tonight score.
// Inputs: tonight's cloud %, humidity %, moon phase (0..1, 0=new, 0.5=full),
// and sunset/moonrise/moonset times so we can hint window.

export interface StargazingInput {
  cloudPct: number; // 0..100
  humidity: number; // 0..100
  moonPhase: number; // 0..1
  sunset?: number;
  moonrise?: number;
  moonset?: number;
}

export interface StargazingScore {
  score: number; // 0-10
  cloudPct: number;
  moonLabel: string;
  moonIllumination: number; // 0..1
  rating: "Excellent" | "Good" | "Fair" | "Poor";
  reason: string;
  bestWindow?: { startTs: number; endTs: number; label: string };
}

const PHASE_LABELS: { name: string; emoji: string }[] = [
  { name: "New Moon", emoji: "🌑" },
  { name: "Waxing Crescent", emoji: "🌒" },
  { name: "First Quarter", emoji: "🌓" },
  { name: "Waxing Gibbous", emoji: "🌔" },
  { name: "Full Moon", emoji: "🌕" },
  { name: "Waning Gibbous", emoji: "🌖" },
  { name: "Last Quarter", emoji: "🌗" },
  { name: "Waning Crescent", emoji: "🌘" },
];

export function moonLabel(phase: number): { name: string; emoji: string } {
  const idx = Math.round(phase * 8) % 8;
  return PHASE_LABELS[idx];
}

export function moonIllumination(phase: number): number {
  // 0=new (0%), 0.5=full (100%), 1=new again. Triangle wave.
  return 1 - Math.abs(phase - 0.5) * 2;
}

export function scoreStargazing(input: StargazingInput): StargazingScore {
  const cloudScore = 10 - (input.cloudPct / 10); // 0% clouds = 10, 100% = 0
  // Humidity above 80% kills transparency.
  const humidityScore =
    input.humidity <= 60 ? 10 : Math.max(0, 10 - (input.humidity - 60) / 4);
  const illumination = moonIllumination(input.moonPhase);
  // Bright moon hurts deep-sky visibility, but full moon nights are still fun.
  const moonScore = 10 - illumination * 5;

  const raw = cloudScore * 0.55 + humidityScore * 0.2 + moonScore * 0.25;
  const score = Math.round(Math.max(0, Math.min(10, raw)) * 10) / 10;

  const phase = moonLabel(input.moonPhase);
  const rating: StargazingScore["rating"] =
    score >= 8 ? "Excellent" : score >= 6 ? "Good" : score >= 4 ? "Fair" : "Poor";

  const reasons: string[] = [];
  if (input.cloudPct < 20) reasons.push("clear sky");
  else if (input.cloudPct < 50) reasons.push("partly cloudy");
  else if (input.cloudPct < 80) reasons.push("mostly cloudy");
  else reasons.push("overcast");
  if (input.humidity > 85) reasons.push("very humid");
  if (illumination > 0.85) reasons.push("bright moon");
  else if (illumination < 0.2) reasons.push("dark moon");

  // Best window: between sunset (or moonset if moon is up) and moonrise next morning,
  // or between moonset and sunrise. Use a simple heuristic.
  let bestWindow: StargazingScore["bestWindow"] | undefined;
  if (input.sunset) {
    const start = input.sunset + 30 * 60; // 30 min after sunset for full astro twilight
    let end = start + 3 * 3600; // default 3h window
    if (input.moonrise && input.moonrise > start) {
      end = Math.min(end, input.moonrise);
    } else if (input.moonset && input.moonset > start) {
      // Moon already up at sunset; window is after moonset
      const afterMoonset = input.moonset + 30 * 60;
      bestWindow = {
        startTs: afterMoonset,
        endTs: afterMoonset + 3 * 3600,
        label: `${formatTime(afterMoonset)} – ${formatTime(afterMoonset + 3 * 3600)} (after moonset)`,
      };
    }
    if (!bestWindow) {
      bestWindow = {
        startTs: start,
        endTs: end,
        label: `${formatTime(start)} – ${formatTime(end)}`,
      };
    }
  }

  return {
    score,
    cloudPct: Math.round(input.cloudPct),
    moonLabel: `${phase.emoji} ${phase.name}`,
    moonIllumination: Math.round(illumination * 100) / 100,
    rating,
    reason: reasons.join(" · "),
    bestWindow,
  };
}

function formatTime(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
